import React from "react";
import { Avatar, Button, Layout, Space, Typography } from "antd";
import { createUseStyles } from "react-jss";
import { LogoutOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import { logoutUser } from "./api/auth.api";
import Chat from "./pages/chat/Index";
import Logo from "./assets/logo-icon-transparent.png";
import { useAuth } from "./hooks/useAuth";

const useStyles = createUseStyles({
  layout: {
    width: "100%",
    height: "100%",
    backgroundColor: "#FFF",
  },
  header: {
    backgroundColor: "#eaf1ea",
    height: "64px",
    display: "flex",
    alignItems: "center",
    padding: "0 24px",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    whiteSpace: "nowrap",
    flexWrap: "nowrap",
    lineHeight: 1,
  },
  logo: {
    height: "56px",
    width: "56px",
    objectFit: "contain",
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
  },
  headerContent: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginLeft: "16px",
  },
  userPill: {
    backgroundColor: "rgba(255, 255, 255, 0.72)",
    borderRadius: "999px",
    padding: "4px 12px 4px 4px",
    display: "flex",
    alignItems: "center",
  },
  content: {
    minHeight: "calc(100vh - 94px)", // total height - header - footer
  },
  footer: {
    padding: "0 20px",
    backgroundColor: "transparent",
    textAlign: "center",
  },
  footerText: {
    fontSize: "12px",
  },
});

const { Header, Footer, Content } = Layout;

const AppLayout: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { user, refreshToken, clearSession } = useAuth();

  const onLogout = async () => {
    try {
      await logoutUser(refreshToken);
    } catch (_error) {
      // Ignore logout failures so local session can still clear.
    } finally {
      clearSession();
      window.location.assign("/login");
    }
  };

  return (
    <Layout className={classes.layout}>
      <Header className={classes.header}>
        <div className={classes.headerContent}>
          <div className={classes.brand}>
            <img
              className={classes.logo}
              alt="ePost Global Assist"
              src={Logo}
            />
            <span className={classes.title}>ePost Assist</span>
          </div>
          <div className={classes.actions}>
            {user?.role === "admin" ? (
              <>
                <Space size="small" className={classes.userPill}>
                  <Avatar style={{ backgroundColor: "#4caf50", flexShrink: 0 }}>
                    {user.username.slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Typography.Text strong style={{ whiteSpace: "nowrap" }}>
                    {user.username}
                  </Typography.Text>
                </Space>
                <Button type="default" onClick={() => navigate("/dashboard")}>
                  Monitor
                </Button>
                <Button icon={<LogoutOutlined />} onClick={onLogout}>
                  Logout
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </Header>
      <Content className={classes.content}>
        <Chat />
      </Content>
      <Footer className={classes.footer}>
        <div className={classes.footerText}>
          &copy; 2025 ePost Assist. All rights reserved.
        </div>
      </Footer>
    </Layout>
  );
};

export default AppLayout;
